import { currentAccountId } from './account'

/**
 * WHAT THE SEASON COST, AND WHAT IS STILL UNKNOWN.
 *
 * EXP-01 and the part of EXP-02 that can be honest without an accounting sync.
 *
 * THE ONE RULE THAT MATTERS IS THE ONE ABOUT MISSING ACTUALS. A job with a
 * three thousand dollar quote and no costs recorded against it is not a three
 * thousand dollar profit; it is a job nobody has entered the bulbs for. Every
 * margin in this file returns null in that case and every screen prints the
 * words rather than a number, because a false ninety percent margin is the
 * most expensive thing a small business dashboard can show: it is the number
 * somebody prices next season off.
 *
 * CASH COLLECTED IS NOT EARNED REVENUE, which EXP-02 states and which this
 * product already keeps apart on the invoices list. Nothing here mixes them:
 * the quote total is what the work is worth, and what has actually arrived is
 * a different figure on a different screen.
 *
 * ALLOCATIONS MUST SUM TO THE TOTAL. A five hundred dollar bulb order split
 * across four houses is the ordinary case in this trade, and a split that does
 * not add up is a cost that has quietly vanished or been counted twice. It is
 * checked as it is typed and refused on the way out.
 *
 * NO RECEIPT EXTRACTION. EXP-01 wants a photograph read for vendor, date and
 * total with a confidence on it. That needs a model and somewhere to keep the
 * document, and the spec's own safeguard is that extraction is only ever a
 * draft. Typing four fields is not the bottleneck; a wrong total that looks
 * machine-read is.
 */

export type Category =
  | 'materials'
  | 'labor'
  | 'travel'
  | 'equipment'
  | 'other'

export const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'materials', label: 'Materials' },
  { id: 'labor', label: 'Labor' },
  { id: 'travel', label: 'Fuel and travel' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'other', label: 'Everything else' },
]

/** Where a slice of a cost went. An empty jobId is the business itself. */
export interface Allocation {
  jobId: string
  amount: number
}

export interface Expense {
  id: string
  /** ISO yyyy-mm-dd. The day on the receipt, not the day it was typed. */
  day: string
  vendor: string
  category: Category
  /** Dollars. The whole cost, before it is split. */
  total: number
  note: string
  /** Where it went. One entry for a whole cost, several for a split. */
  allocations: Allocation[]
  at: string
}

const keyFor = () => `lightsos.expenses.${currentAccountId()}`

const cents = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100)

/**
 * DOES THE SPLIT ADD UP.
 *
 * Compared in cents, because 33.33 three times is 99.99 in floating point and
 * a rounding complaint on a form somebody filled in correctly is how a feature
 * gets worked around instead of used.
 */
export function allocationProblem(e: Pick<Expense, 'total' | 'allocations'>): string | null {
  if (!Number.isFinite(e.total) || e.total <= 0) return 'The total is not an amount above zero.'
  if (e.allocations.length === 0) return 'This has to go somewhere, even if that is the business.'
  if (e.allocations.some((a) => !Number.isFinite(a.amount) || a.amount < 0)) {
    return 'Every share has to be an amount.'
  }
  const sum = e.allocations.reduce((n, a) => n + cents(a.amount), 0)
  const want = cents(e.total)
  if (sum === want) return null
  const off = (sum - want) / 100
  return off > 0
    ? `The shares add up to ${off.toFixed(2)} more than the total.`
    : `The shares are ${Math.abs(off).toFixed(2)} short of the total.`
}

/** Everything spent on one job, over every expense that names it. */
export function spentOn(jobId: string, list: Expense[]): number {
  let n = 0
  for (const e of list) {
    for (const a of e.allocations) {
      if (a.jobId === jobId) n += cents(a.amount)
    }
  }
  return n / 100
}

/** Everything that went to the business rather than to a house. */
export const overhead = (list: Expense[]): number => spentOn('', list)

export interface ByCategory {
  category: Category
  label: string
  total: number
}

export function byCategory(list: Expense[], jobId?: string): ByCategory[] {
  return CATEGORIES.map((c) => {
    let n = 0
    for (const e of list) {
      if (e.category !== c.id) continue
      for (const a of e.allocations) {
        if (jobId === undefined || a.jobId === jobId) n += cents(a.amount)
      }
    }
    return { category: c.id, label: c.label, total: n / 100 }
  }).filter((c) => c.total > 0)
}

/**
 * WHAT A JOB MADE, OR NOTHING AT ALL.
 *
 * The margin is null whenever there are no costs recorded, and that is the
 * whole point rather than an edge case. EXP-02: do not show false profit for
 * missing actuals. A job with a quote and no expenses against it shows one
 * hundred percent margin in every naive version of this screen, and that is
 * the number a person prices next season off.
 */
export interface JobResult {
  jobId: string
  /** What the work is worth. Not what has been collected. */
  worth: number
  /** What has actually been entered against it. */
  cost: number
  /** Null when nothing has been recorded, which is not the same as zero cost. */
  margin: number | null
  /** Null for the same reason. */
  marginPct: number | null
}

export function jobResult(jobId: string, worth: number, list: Expense[]): JobResult {
  const cost = spentOn(jobId, list)
  const anything = list.some((e) => e.allocations.some((a) => a.jobId === jobId))
  return {
    jobId,
    worth,
    cost,
    margin: anything ? worth - cost : null,
    marginPct: anything && worth > 0 ? Math.round(((worth - cost) / worth) * 100) : null,
  }
}

/** How many of these jobs have nothing recorded against them at all. */
export const missingActuals = (results: JobResult[]): number =>
  results.filter((r) => r.margin === null).length

/* --------------------------------------------------------------- storage */

export function normalizeExpenses(raw: unknown): Expense[] {
  if (!Array.isArray(raw)) return []
  const out: Expense[] = []
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const e = r as Partial<Expense>
    if (typeof e.id !== 'string' || !e.id) continue
    if (!CATEGORIES.some((c) => c.id === e.category)) continue
    if (!Number.isFinite(e.total)) continue
    const allocations = Array.isArray(e.allocations)
      ? e.allocations
          .filter((a) => a && typeof a === 'object' && Number.isFinite((a as Allocation).amount))
          .map((a) => ({
            jobId: typeof (a as Allocation).jobId === 'string' ? (a as Allocation).jobId : '',
            amount: Number((a as Allocation).amount),
          }))
      : []
    out.push({
      id: e.id,
      day: typeof e.day === 'string' ? e.day.slice(0, 10) : '',
      vendor: typeof e.vendor === 'string' ? e.vendor : '',
      category: e.category as Category,
      total: Number(e.total),
      note: typeof e.note === 'string' ? e.note : '',
      // A cost that lost its split on the way off disk goes to the business
      // rather than to nowhere, so it is still counted somewhere.
      allocations: allocations.length ? allocations : [{ jobId: '', amount: Number(e.total) }],
      at: typeof e.at === 'string' ? e.at : '',
    })
  }
  return out
}

export const loadExpenses = (): Expense[] => {
  try {
    const raw = localStorage.getItem(keyFor())
    return raw ? normalizeExpenses(JSON.parse(raw)) : []
  } catch {
    return []
  }
}

export const saveExpenses = (list: Expense[]) => {
  try {
    localStorage.setItem(keyFor(), JSON.stringify(list))
  } catch {
    /* private mode */
  }
}

export const newExpense = (id: string, day: string): Expense => ({
  id,
  day,
  vendor: '',
  category: 'materials',
  total: 0,
  note: '',
  allocations: [{ jobId: '', amount: 0 }],
  at: new Date().toISOString(),
})

export const NOT_IN_COSTS: string[] = [
  'Reading a receipt photograph. That needs a model and somewhere to keep the document, and the spec is explicit that extraction is only ever a draft anyway.',
  'Labor hours. Starting and finishing a stop records when somebody was at a house; nothing turns that into a wage, and guessing at one would put a wrong number in a margin.',
  'Tax, depreciation and how overhead is spread. Those are accounting decisions and this is a notebook.',
  'Any sync to accounting software. Nothing here leaves the device.',
]
